import { useEffect, useRef, useState } from "preact/hooks";
import { call, groupCalls, type CallMedia, type CallView, type PeerView } from "../calls";
import { callLabel, type Msg } from "../db";
import * as S from "../state";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import { drawer, engine, toast } from "./Main";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function startCall(jid: string, media: CallMedia) {
  const e = engine.value;
  if (!e) return;
  e.calls.start(jid, media).catch((err) => toast(errText(err)));
}

/** An event's call link: answer it if it's ringing here, else join the
 * chat's ongoing call or start one. */
export function joinCall(jid: string, media: CallMedia) {
  const e = engine.value;
  const c = call.value;
  if (!e) return;
  if (c?.phase === "incoming" && c.chat === jid) return void e.calls.accept(media === "video").catch((err) => toast(errText(err)));
  if (c && c.phase !== "ended") return toast(c.chat === jid ? "You're already in this call" : "You're already in a call");
  startCall(jid, media);
}

export function fmtDuration(s: number) {
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, "0");
  return m >= 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}:${ss}` : `${String(m).padStart(2, "0")}:${ss}`;
}

// ---- chat header buttons -------------------------------------------------------------

export function CallButtons({ jid }: { jid: string }) {
  const group = S.isGroup(jid);
  const blocked = S.blocked.value.has(jid);
  const g = group ? groupCalls.value.get(jid) : undefined;
  const inCall = !!call.value && call.value.phase !== "ended";
  if (blocked) return null;
  if (g && call.value?.sid !== g.sid)
    return (
      <button class="join-call-btn" onClick={() => void join(jid, g.sid)} disabled={inCall}>
        <Icon name={g.media === "video" ? "videocam" : "phone"} size={18} /> Join
      </button>
    );
  return (
    <div class="call-btns">
      <button class="icon-btn" aria-label="Video call" title="Video call" disabled={inCall} onClick={() => startCall(jid, "video")}>
        <Icon name="videocam" />
      </button>
      <button class="icon-btn" aria-label="Voice call" title="Voice call" disabled={inCall} onClick={() => startCall(jid, "audio")}>
        <Icon name="phone" />
      </button>
    </div>
  );
}

function join(room: string, sid: string) {
  return engine.value?.calls.joinGroup(room, sid).catch((err) => toast(errText(err)));
}

// ---- call history entry in the chat ---------------------------------------------------

export function CallBubble({ m, tail }: { m: Msg; tail: boolean }) {
  const c = m.call;
  const out = m.dir === "out";
  const missed = !out && !c?.group && c?.result !== "answered";
  const active = c?.group && groupCalls.value.get(m.chat)?.sid === c.sid && call.value?.sid !== c.sid;
  const icon = c?.media === "video" ? "videocam" : "phone";
  const label = c?.group ? (out ? "You started a " : S.memberName(m.from) + " started a ") + (c.media === "video" ? "video call" : "voice call") : callLabel(c, m.dir);
  const detail = c?.group
    ? c.result === "ended"
      ? `Ended${c.duration ? " · " + fmtDuration(c.duration) : ""}`
      : active
        ? `${groupCalls.value.get(m.chat)?.members.size ?? 1} in call`
        : ""
    : c?.result === "answered" && c.duration
      ? fmtDuration(c.duration)
      : "";
  return (
    <div class={"row " + m.dir + (tail ? " tail" : "")} data-key={m.key}>
      <div class={"bubble call-bubble" + (missed ? " missed" : "")}>
        <button
          class="call-entry"
          title={c?.group ? undefined : "Call again"}
          onClick={() => (c?.group ? active && void join(m.chat, c.sid) : startCall(m.chat, c?.media ?? "audio"))}
        >
          <span class="call-entry-icon">
            <Icon name={icon} size={20} />
          </span>
          <span class="call-entry-text">
            <span class="call-entry-label">{label}</span>
            <span class="call-entry-detail">
              <Icon name={missed ? "callMissed" : out ? "callMade" : "callReceived"} size={14} />
              {detail || timeOf(m.ts)}
            </span>
          </span>
          {active && <span class="call-join">Join</span>}
        </button>
        <span class="meta">{timeOf(m.ts)}</span>
      </div>
    </div>
  );
}

const timeOf = (ts: number) => new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

// ---- call screen ------------------------------------------------------------------------

export function CallOverlay() {
  const v = call.value;
  if (!v) return null;
  if (v.phase === "incoming") return <Incoming v={v} />;
  return <CallScreen v={v} />;
}

function Incoming({ v }: { v: CallView }) {
  const e = engine.value!;
  const name = S.displayName(v.caller);
  const prof = S.profiles.value.get(v.caller);
  const what = v.media === "video" ? "video call" : "voice call";
  return (
    <div class="incoming-call" role="dialog" aria-label="Incoming call">
      <Avatar url={v.group ? S.groups.value.get(v.chat)?.avatar_url : prof?.avatar_url} jid={v.group ? v.chat : v.caller} name={v.group ? S.displayName(v.chat) : name} size={72} group={v.group} />
      <div class="incoming-text">
        <strong>{v.group ? S.displayName(v.chat) : name}</strong>
        {!v.group && prof?.name && prof.name !== name && <small>~{prof.name}</small>}
        <span>{v.group ? `${name} is inviting you to a group ${what}` : `Incoming ${what}`}</span>
        <small>
          <Icon name="lock" size={11} /> End-to-end encrypted
        </small>
      </div>
      <div class="incoming-actions">
        <button class="call-ctl end" aria-label="Decline" onClick={() => void e.calls.decline()}>
          <Icon name="callEnd" />
        </button>
        <button class="call-ctl accept" aria-label="Accept" onClick={() => e.calls.accept().catch((err) => toast(errText(err)))}>
          <Icon name={v.media === "video" ? "videocam" : "phone"} />
        </button>
      </div>
    </div>
  );
}

function CallScreen({ v }: { v: CallView }) {
  const e = engine.value!;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const title = S.displayName(v.chat);
  const reconnecting = v.peers.some((p) => p.state === "reconnecting");
  const status =
    v.phase === "ended"
      ? v.ended
      : reconnecting
        ? "Reconnecting…"
        : v.phase === "active" && v.startedAt
          ? fmtDuration(Math.max(0, Math.round((now - v.startedAt) / 1000)))
          : v.phase === "ringing"
            ? "Ringing…"
            : v.phase === "outgoing"
              ? v.group
                ? "Waiting for others to join…"
                : "Calling…"
              : "Connecting…";
  const sending = v.camera || v.screen;
  const oneToOne = !v.group;
  const peer = v.peers[0];
  const canShare = typeof navigator.mediaDevices?.getDisplayMedia === "function" && !/Android|iPhone|iPad/i.test(navigator.userAgent);

  return (
    <div class={"call-screen" + (v.phase === "ended" ? " ended" : "")} role="dialog" aria-label={v.media === "video" ? "Video call" : "Voice call"}>
      {v.peers.map((p) => (
        <RemoteAudio key={"a" + p.key} stream={p.stream} />
      ))}
      <div class={"call-stage" + (oneToOne ? " one" : " grid n" + Math.min(v.peers.length, 9))}>
        {oneToOne ? (
          peer?.video && peer.stream.getVideoTracks().length ? (
            <Video stream={peer.stream} class={"remote-video" + (peer.screen ? " screen" : "")} />
          ) : (
            <PeerCard jid={v.chat} />
          )
        ) : v.peers.length ? (
          v.peers.map((p) => <Tile key={p.key} p={p} />)
        ) : (
          <PeerCard jid={v.chat} group />
        )}
      </div>
      <header class="call-head">
        <strong class="call-title">{title}</strong>
        <span class="call-status">{status}</span>
        <small class="call-e2e">
          <Icon name="lock" size={11} /> End-to-end encrypted
        </small>
        {oneToOne && peer?.muted && <span class="call-peer-muted">{S.displayName(v.chat)} muted their microphone</span>}
      </header>
      {sending && v.local && v.phase !== "ended" && <Video stream={v.local} muted class={"local-video" + (v.screen ? "" : " mirror")} />}
      {v.phase !== "ended" && (
        <div class="call-controls">
          <button class={"call-ctl" + (v.muted ? " on" : "")} aria-label={v.muted ? "Unmute" : "Mute"} aria-pressed={v.muted} onClick={() => e.calls.toggleMute()}>
            <Icon name={v.muted ? "micOff" : "mic"} />
          </button>
          <button
            class={"call-ctl" + (!v.camera ? " on" : "")}
            aria-label={v.camera ? "Turn camera off" : "Turn camera on"}
            onClick={() => e.calls.toggleCamera().catch((err) => toast(errText(err)))}
          >
            <Icon name={v.camera ? "videocam" : "videocamOff"} />
          </button>
          {v.canFlip && (
            <button class="call-ctl" aria-label="Switch camera" onClick={() => e.calls.flipCamera().catch((err) => toast(errText(err)))}>
              <Icon name="flip" />
            </button>
          )}
          {canShare && (
            <button
              class={"call-ctl" + (v.screen ? " sharing" : "")}
              aria-label={v.screen ? "Stop sharing screen" : "Share screen"}
              onClick={() => e.calls.toggleScreen().catch((err) => (err as DOMException).name !== "NotAllowedError" && toast(errText(err)))}
            >
              <Icon name="screenShare" />
            </button>
          )}
          <button class="call-ctl end" aria-label="End call" onClick={() => void e.calls.hangup()}>
            <Icon name="callEnd" />
          </button>
        </div>
      )}
    </div>
  );
}

function PeerCard({ jid, group }: { jid: string; group?: boolean }) {
  const prof = group ? undefined : S.profiles.value.get(jid);
  const avatar = group ? S.groups.value.get(jid)?.avatar_url : prof?.avatar_url;
  return (
    <div class="peer-card">
      <Avatar url={avatar} jid={jid} name={S.displayName(jid)} size={128} group={group} />
    </div>
  );
}

function Tile({ p }: { p: PeerView }) {
  const prof = S.profiles.value.get(p.jid);
  return (
    <div class={"call-tile" + (p.state !== "connected" ? " pending" : "")}>
      {p.video && p.stream.getVideoTracks().length ? (
        <Video stream={p.stream} class="tile-video" />
      ) : (
        <Avatar url={prof?.avatar_url} jid={p.jid} name={S.memberName(p.jid)} size={72} />
      )}
      <span class="tile-name">
        {p.muted && <Icon name="micOff" size={14} />}
        {S.memberName(p.jid)}
        {p.state !== "connected" && " · " + (p.state === "reconnecting" ? "reconnecting" : "connecting")}
      </span>
    </div>
  );
}

function Video({ stream, muted, class: cls }: { stream: MediaStream; muted?: boolean; class: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    void el.play().catch(() => {});
  });
  // Always muted: sound plays through RemoteAudio (one element per peer).
  return <video ref={ref} class={cls} autoPlay playsInline muted={muted ?? true} />;
}

function RemoteAudio({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    void el.play().catch(() => {});
  });
  return <audio ref={ref} autoPlay class="remote-audio" />;
}

// ---- Calls screen (history) ----------------------------------------------------------------

export function CallsPanel() {
  const [list, setList] = useState<Msg[] | null>(null);
  const live = call.value?.phase;
  const chats = S.chats.value;
  useEffect(() => {
    void engine.value?.callHistory().then(setList);
  }, [live, chats]);
  return (
    <div class="drawer" role="dialog" aria-label="Calls">
      <header class="drawer-head">
        <button class="icon-btn" onClick={() => (drawer.value = "none")} aria-label="Back">
          <Icon name="back" />
        </button>
        <h2>Calls</h2>
      </header>
      <div class="drawer-body">
        <p class="muted pad small">
          <Icon name="lock" size={12} /> Your personal calls are end-to-end encrypted.
        </p>
        {list === null ? null : list.length === 0 ? (
          <p class="muted pad">No calls yet. Start one from a chat with the phone or camera button.</p>
        ) : (
          <ul class="call-log">
            {list.map((m) => {
              const c = m.call!;
              const missed = m.dir === "in" && !c.group && c.result !== "answered";
              const prof = S.profiles.value.get(m.chat);
              const group = S.isGroup(m.chat);
              return (
                <li key={m.key}>
                  <button
                    class="call-log-who"
                    onClick={() => {
                      drawer.value = "none";
                      void engine.value?.openChat(m.chat);
                    }}
                  >
                    <Avatar url={group ? S.groups.value.get(m.chat)?.avatar_url : prof?.avatar_url} jid={m.chat} name={S.displayName(m.chat)} size={44} group={group} />
                    <span class="grow">
                      <span class={"chat-name" + (missed ? " missed" : "")}>{S.displayName(m.chat)}</span>
                      <small class="call-log-detail">
                        <Icon name={missed ? "callMissed" : m.dir === "out" ? "callMade" : "callReceived"} size={14} />
                        {whenLabel(m.ts)}
                        {c.result === "answered" && c.duration ? " · " + fmtDuration(c.duration) : ""}
                      </small>
                    </span>
                  </button>
                  {!group && (
                    <button class="icon-btn" aria-label={c.media === "video" ? "Video call " + S.displayName(m.chat) : "Voice call " + S.displayName(m.chat)} onClick={() => startCall(m.chat, c.media)}>
                      <Icon name={c.media === "video" ? "videocam" : "phone"} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function whenLabel(ts: number) {
  const d = new Date(ts);
  const today = new Date();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (d.toDateString() === today.toDateString()) return "Today, " + time;
  const y = new Date(today);
  y.setDate(today.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Yesterday, " + time;
  return d.toLocaleDateString([], { day: "numeric", month: "long" }) + ", " + time;
}


